import { useState, useEffect, useCallback } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  Link,
  useLocation,
} from 'react-router-dom';
import type { FC } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Signups from './pages/Signups';
import Deposits from './pages/Deposits';
import Engagement from './pages/Engagement';
import { ControlBar } from './components/ControlBar';
import { opsFetch } from './utils/api';
import { defaultRange } from './utils/format';
import type { DailyMetrics, Grouping, OpsContext } from './types';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

function ProtectedLayout() {
  const { token, loading: authLoading, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const [range, setRange] = useState(() => defaultRange(30));
  const [grouping, setGrouping] = useState<Grouping>('day');
  const [current, setCurrent] = useState<DailyMetrics | null>(null);
  const [history, setHistory] = useState<DailyMetrics[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setDataLoading(true);
    setDataError('');
    try {
      const [c, h] = await Promise.all([
        opsFetch<{ metrics: DailyMetrics | null }>('/ops/metrics/current', token),
        opsFetch<{ metrics: DailyMetrics[] }>(
          `/ops/metrics/daily?from=${range.from}&to=${range.to}`,
          token,
        ),
      ]);
      setCurrent(c.metrics);
      setHistory(h.metrics);
    } catch (err) {
      setDataError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setDataLoading(false);
    }
  }, [token, range]);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading) {
    return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const ctx: OpsContext = { current, history, grouping };

  return (
    <div className="d-flex flex-column vh-100">
      <Header onLogout={() => setShowLogoutConfirm(true)} />
      <ControlBar
        range={range}
        onRangeChange={setRange}
        grouping={grouping}
        onGroupingChange={setGrouping}
      />
      <PageTabs />
      <main className="flex-grow-1 overflow-auto px-3 pb-3">
        {dataLoading && (
          <div className="text-center text-muted py-3">
            <div className="spinner-border spinner-border-sm" /> Loading…
          </div>
        )}
        {dataError && <div className="alert alert-danger">{dataError}</div>}
        <Outlet context={ctx} />
      </main>
      <Footer />

      {showLogoutConfirm && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h6 className="modal-title">Log Out</h6>
              </div>
              <div className="modal-body">
                <div className="small">Are you sure you want to log out?</div>
              </div>
              <div className="modal-footer py-1">
                <button className="btn btn-sm btn-secondary" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
                <button className="btn btn-sm btn-danger" onClick={() => { setShowLogoutConfirm(false); logout(); }}>Log Out</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Header: FC<{ onLogout: () => void }> = ({ onLogout }) => (
  <nav className="navbar navbar-dark bg-dark flex-shrink-0 py-1">
    <div className="container-fluid px-3">
      <span className="navbar-brand mb-0 small">Polyreader Ops</span>
      <button onClick={onLogout} className="btn btn-outline-light btn-sm">Logout</button>
    </div>
  </nav>
);

const TABS: { path: string; label: string }[] = [
  { path: '/signups', label: 'Signups' },
  { path: '/deposits', label: 'Deposits' },
  { path: '/engagement', label: 'Engagement' },
];

const PageTabs: FC = () => {
  const location = useLocation();
  return (
    <ul className="nav nav-tabs px-3 pt-3">
      {TABS.map((t) => (
        <li className="nav-item" key={t.path}>
          <Link
            to={t.path}
            className={`nav-link ${location.pathname.startsWith(t.path) ? 'active' : ''}`}
          >
            {t.label}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const Footer: FC = () => (
  <footer className="bg-dark text-light text-center flex-shrink-0 py-1">
    <small className="text-light text-opacity-50">Polyreader ops — read-only internal</small>
  </footer>
);

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    element: <ProtectedLayout />,
    children: [
      { path: '/', element: <Navigate to="/signups" replace /> },
      { path: '/signups', element: <Signups /> },
      { path: '/deposits', element: <Deposits /> },
      { path: '/engagement', element: <Engagement /> },
      // Back-compat: any old /metrics link lands on Signups.
      { path: '/metrics', element: <Navigate to="/signups" replace /> },
    ],
  },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
